import React from "react";
import Konva from "konva";
import { Group, Rect, Text as KText, Shape, Line, Image as KImage } from "react-konva";
import type { Element, ImageElement as ImageEl, RuntimeMedia, VideoElement as VideoEl } from "../types";
import { borderDashForStyle, normalizeBorder } from "../utils/border";
import { normalizeCurve, normalizeCornerCurve, roundedRectWithCurvePath } from "./utils/shapePaths";
import { needsFaceBlurProcessing } from "./utils/faceBlurRenderer";
import type { KonvaNodeCommonProps } from "./CanvasStageTypes";
import { FeatheredGroup, FilteredImage, buildKonvaFilters, generateFilterSignature } from "./CanvasStageFilters";
import type { VideoFilterPreviewQuality } from "./runtimePlaybackBudget";
import {
  borderAlignDeltaPx,
  clampCropRect,
  colorWithAlpha,
  drawMaskPathOnCtx,
  konvaCornerRadiusProp,
  maskClipFunc,
  resolveCornerRadiiPx,
} from "./CanvasStageElementUtils";
import { getHtmlImageStatus, getHtmlVideoStatus } from "../utils/htmlMedia";
import { shouldProtectLongVideoPlayback } from "../utils/longVideoRuntime";
import { getScrubBitmap } from "../engine/ScrubBitmapCache";

export type RenderImageElementArgs = {
  el: ImageEl;
  common: KonvaNodeCommonProps;
  globalCompositeOperation: GlobalCompositeOperation;
  runtime: React.MutableRefObject<RuntimeMedia>;
  faceBlurCacheRef: React.MutableRefObject<Map<string, HTMLCanvasElement | ImageBitmap | null>>;
  gradientFadeCacheRef?: React.MutableRefObject<Map<string, ImageBitmap | null>>;
  hoveredElementId: string | null;
  totalViewportScale: number;
  isTransformingRef: React.RefObject<boolean>;
  cropGuidesEnabled: boolean;
  cropGuideMode?: string;
  isCropTarget: boolean;
  cropDragRef: React.MutableRefObject<Map<string, { x: number; y: number; width: number; height: number }>>;
  updateEl: (id: string, patch: Partial<Element>) => void;
};

// Render crop guide lines based on mode
function renderCropGuideLines(
  mode: string,
  boundsW: number,
  boundsH: number,
  totalViewportScale: number
): React.ReactNode[] {
  const stroke = "rgba(255,255,255,0.55)";
  const sw = Math.max(1, 1 / Math.max(0.001, totalViewportScale));
  const lineProps = { stroke, strokeWidth: sw, listening: false, perfectDrawEnabled: false };

  if (mode === "thirds") {
    return [
      <Line key="v1" points={[boundsW / 3, 0, boundsW / 3, boundsH]} {...lineProps} />,
      <Line key="v2" points={[(boundsW * 2) / 3, 0, (boundsW * 2) / 3, boundsH]} {...lineProps} />,
      <Line key="h1" points={[0, boundsH / 3, boundsW, boundsH / 3]} {...lineProps} />,
      <Line key="h2" points={[0, (boundsH * 2) / 3, boundsW, (boundsH * 2) / 3]} {...lineProps} />,
    ];
  }
  if (mode === "grid") {
    // 4×4 grid (3 lines each direction)
    return [
      <Line key="v1" points={[boundsW / 4, 0, boundsW / 4, boundsH]} {...lineProps} />,
      <Line key="v2" points={[boundsW / 2, 0, boundsW / 2, boundsH]} {...lineProps} />,
      <Line key="v3" points={[(boundsW * 3) / 4, 0, (boundsW * 3) / 4, boundsH]} {...lineProps} />,
      <Line key="h1" points={[0, boundsH / 4, boundsW, boundsH / 4]} {...lineProps} />,
      <Line key="h2" points={[0, boundsH / 2, boundsW, boundsH / 2]} {...lineProps} />,
      <Line key="h3" points={[0, (boundsH * 3) / 4, boundsW, (boundsH * 3) / 4]} {...lineProps} />,
    ];
  }
  if (mode === "center") {
    // Center crosshair + diagonals
    return [
      <Line key="cv" points={[boundsW / 2, 0, boundsW / 2, boundsH]} {...lineProps} />,
      <Line key="ch" points={[0, boundsH / 2, boundsW, boundsH / 2]} {...lineProps} />,
      <Line key="d1" points={[0, 0, boundsW, boundsH]} {...lineProps} stroke="rgba(255,255,255,0.3)" />,
      <Line key="d2" points={[boundsW, 0, 0, boundsH]} {...lineProps} stroke="rgba(255,255,255,0.3)" />,
    ];
  }
  return [];
}

export function renderImageElement({
  el,
  common,
  globalCompositeOperation,
  runtime,
  faceBlurCacheRef,
  gradientFadeCacheRef,
  hoveredElementId,
  totalViewportScale,
  isTransformingRef,
  cropGuidesEnabled,
  cropGuideMode,
  isCropTarget,
  cropDragRef,
  updateEl,
}: RenderImageElementArgs) {
  const originalImg = runtime.current.imageMap.get(el.id);
  const imageEl = el;

  // Use face blur processed canvas/bitmap if available, otherwise original image
  const faceBlurSettings = (imageEl as any).faceBlur;
  const hasFaceBlur = needsFaceBlurProcessing(faceBlurSettings);
  const faceBlurCanvas = hasFaceBlur ? faceBlurCacheRef.current.get(el.id) : null;
  const img = faceBlurCanvas || originalImg;
  // sourceMedia needs an HTML element (not ImageBitmap) for export-time effects
  const sourceMediaFallback = faceBlurCanvas instanceof HTMLCanvasElement ? faceBlurCanvas : undefined;

  // Check for gradient fade effect
  const gradientFadeBitmap = gradientFadeCacheRef?.current.get(el.id);
  const opSettings = (el as any).opacitySettings;
  const useGradientFade = opSettings?.mode === 'gradient-fade' && gradientFadeBitmap;

  // If gradient fade is active, render the pre-composited bitmap
  if (useGradientFade && gradientFadeBitmap) {
    return (
      <KImage
        key={el.id}
        {...common}
        image={gradientFadeBitmap}
        width={el.width}
        height={el.height}
        vbElementId={el.id}
        vbSource={originalImg || undefined}
        vbFaceBlur={faceBlurSettings}
        globalCompositeOperation={globalCompositeOperation}
      />
    );
  }

  // Check if this is a placeholder image (no source loaded)
  const isPlaceholder = !img && (!imageEl.src || imageEl.src === "" || imageEl.src.startsWith("{{"));

  if (isPlaceholder) {
    // Render placeholder for image elements without source
    const placeholderName = imageEl.name || el.id;
    const label = placeholderName.includes("{{") ? placeholderName : "Drop image here";
    return (
      <Group key={el.id} {...common} width={el.width} height={el.height}>
        <Rect
          x={0}
          y={0}
          width={el.width}
          height={el.height}
          fill="#1a1a2e"
          stroke="rgba(59,130,246,0.4)"
          strokeWidth={2}
          dash={[8, 4]}
          cornerRadius={4}
        />
        <KText
          x={0}
          y={0}
          width={el.width}
          height={el.height}
          text={label}
          fontSize={Math.min(24, Math.max(12, el.width / 12))}
          fill="rgba(255,255,255,0.6)"
          align="center"
          verticalAlign="middle"
          listening={false}
        />
      </Group>
    );
  }

  const boundsW = Math.max(1, Number(el.width) || 1);
  const boundsH = Math.max(1, Number(el.height) || 1);
  const radiiPx = resolveCornerRadiiPx(imageEl.cornerRadius, boundsW, boundsH, imageEl);
  const curve = normalizeCurve(imageEl);
  const cornerCurve = normalizeCornerCurve(imageEl);
  const border = normalizeBorder(imageEl);
  const borderW = border.enabled ? border.width : 0;
  const borderStroke = border.color ? colorWithAlpha(border.color, border.opacity) : undefined;
  const borderDash = borderDashForStyle(border.style, borderW);
  const mask = (imageEl as any)?.mask as any;
  const maskEnabled = !!mask?.enabled;
  const maskFeather = Math.max(0, Number(mask?.feather) || 0);
  const needsRoundedClip = radiiPx.some((v) => v > 0.001);
  const needsCustomClip = needsRoundedClip || !!curve;
  // Media rule: border renders outside the content mask.
  const alignDelta = borderAlignDeltaPx("outside", borderW);
  const borderW2 = Math.max(1, boundsW - 2 * alignDelta);
  const borderH2 = Math.max(1, boundsH - 2 * alignDelta);
  const borderRadii = radiiPx.map((r) => Math.max(0, r - alignDelta)) as [number, number, number, number];
  const needsBorder = borderW > 0.001 && !!borderStroke;

  // Get image dimensions - handle both HTMLImageElement and HTMLCanvasElement
  const iw = img
    ? (img instanceof HTMLCanvasElement ? img.width : (img as HTMLImageElement).naturalWidth || 0)
    : 0;
  const ih = img
    ? (img instanceof HTMLCanvasElement ? img.height : (img as HTMLImageElement).naturalHeight || 0)
    : 0;

  const rawContentRect = (imageEl as any)?.contentRect as { x: number; y: number; width: number; height: number } | undefined;

  // Build Konva filters from our filter format (including HSL/curves from photoAdjustments and LUT)
  const elementFilters = imageEl.filters || [];
  const elLutSettings = imageEl.lutSettings;
  const imageFilterSignature = generateFilterSignature(elementFilters, imageEl.photoAdjustments, undefined, elLutSettings);
  const { konvaFilters, props: filterProps, gpuFilters } = buildKonvaFilters(elementFilters, imageEl.photoAdjustments, undefined, elLutSettings);

  const hasRawContentRect =
    !!rawContentRect &&
    Number.isFinite(rawContentRect.x) &&
    Number.isFinite(rawContentRect.y) &&
    Number.isFinite(rawContentRect.width) &&
    Number.isFinite(rawContentRect.height) &&
    rawContentRect.width > 0 &&
    rawContentRect.height > 0;

  // Use element's fitMode (default to 'cover' for backward compatibility)
  const fitMode = (imageEl as any).fitMode === "contain" ? "contain" : "cover";
  // Calculate autoFitRect in NATURAL source coordinates (for crop), not frame coordinates
  const autoFitRect =
    !hasRawContentRect && iw > 0 && ih > 0
      ? (() => {
          if (fitMode === "contain") {
            // CONTAIN: Show full source (no crop)
            return { x: 0, y: 0, width: iw, height: ih };
          } else {
            // COVER: Calculate which portion of source fills the frame
            const frameAspect = boundsW / boundsH;
            const mediaAspect = iw / ih;
            if (mediaAspect > frameAspect) {
              // Media is wider - crop sides
              const visibleWidth = ih * frameAspect;
              const cropX = (iw - visibleWidth) / 2;
              return { x: cropX, y: 0, width: visibleWidth, height: ih };
            } else {
              // Media is taller - crop top/bottom
              const visibleHeight = iw / frameAspect;
              const cropY = (ih - visibleHeight) / 2;
              return { x: 0, y: cropY, width: iw, height: visibleHeight };
            }
          }
        })()
      : undefined;

  const contentRect = hasRawContentRect ? rawContentRect : autoFitRect;

  const hasContentRect =
    !!contentRect &&
    Number.isFinite(contentRect.x) &&
    Number.isFinite(contentRect.y) &&
    Number.isFinite(contentRect.width) &&
    Number.isFinite(contentRect.height) &&
    contentRect.width > 0 &&
    contentRect.height > 0;

  // Calculate crop for Image element - always use Image's built-in crop
  // This is more reliable than Group clipping which doesn't work in all cases
  const crop = hasContentRect && iw > 0 && ih > 0
    ? (() => {
        // contentRect is in natural image coordinates
        // Clamp to valid bounds
        const cropX = Math.max(0, Math.min(iw, contentRect.x));
        const cropY = Math.max(0, Math.min(ih, contentRect.y));
        const cropW = Math.max(1, Math.min(iw - cropX, contentRect.width));
        const cropH = Math.max(1, Math.min(ih - cropY, contentRect.height));
        return { x: cropX, y: cropY, width: cropW, height: cropH };
      })()
    : undefined;

  // Draw position: always at 0,0 since crop handles the source selection
  const drawX = 0;
  const drawY = 0;
  // Draw size: frame bounds - Image will scale the cropped source to fit
  const drawW = boundsW;
  const drawH = boundsH;

  const clipCacheKey = maskEnabled
    ? [
        String(mask?.type || "rect"),
        String(mask?.x ?? 0),
        String(mask?.y ?? 0),
        String(mask?.scale ?? 1),
        String(mask?.rotation ?? 0),
        String(mask?.polygonSides ?? ""),
        String(mask?.customPath ?? ""),
        String(curve?.type ?? ""),
        String(curve?.strength ?? ""),
        String(boundsW),
        String(boundsH),
      ].join("|")
    : "";

  const maskClipProps = maskEnabled
    ? {
        clipFunc: maskClipFunc({ w: boundsW, h: boundsH, mask, radiiPx, curve }),
      }
    : (needsCustomClip
        ? {
            clipFunc: (ctx: any) => {
              roundedRectWithCurvePath(ctx as any, boundsW, boundsH, radiiPx, curve, cornerCurve);
            },
          }
        : undefined);

  const showMaskOutline = maskEnabled && hoveredElementId === el.id;

  // Needs a background behind the media for masks to render correctly
  const bgLayer = needsCustomClip ? (
    <Rect
      x={0}
      y={0}
      width={boundsW}
      height={boundsH}
      fill="rgba(0,0,0,0.01)"
      listening={false}
    />
  ) : null;

  // Contain mode background (letterbox/pillarbox fill)
  const containBgStyle = (imageEl as any).containBgStyle || "transparent";
  const containBgColor = (imageEl as any).containBgColor || "#000000";
  const containBgBlur = (imageEl as any).containBgBlur ?? 20;
  const needsContainBg = fitMode === "contain" && containBgStyle !== "transparent" && hasContentRect;

  const containBgLayer = needsContainBg ? (
    containBgStyle === "color" ? (
      <Rect
        x={0}
        y={0}
        width={boundsW}
        height={boundsH}
        fill={containBgColor}
        listening={false}
      />
    ) : containBgStyle === "blur" && img ? (
      <KImage
        x={0}
        y={0}
        width={boundsW}
        height={boundsH}
        image={img}
        filters={[Konva.Filters.Blur]}
        blurRadius={containBgBlur}
        listening={false}
      />
    ) : containBgStyle === "mirror" && img ? (
      // Mirror edges: scale up and flip - simplified version
      <KImage
        x={-boundsW * 0.1}
        y={-boundsH * 0.1}
        width={boundsW * 1.2}
        height={boundsH * 1.2}
        image={img}
        filters={[Konva.Filters.Blur]}
        blurRadius={10}
        listening={false}
      />
    ) : null
  ) : null;

  const cropRect = hasContentRect ? contentRect : autoFitRect;
  const cropOverlay = isCropTarget && cropRect ? (
    <Group>
      <Rect
        x={0}
        y={0}
        width={boundsW}
        height={boundsH}
        stroke="rgba(251, 191, 36, 0.9)"
        strokeWidth={Math.max(1, 1 / Math.max(0.001, totalViewportScale))}
        listening={false}
      />
      {cropGuidesEnabled && renderCropGuideLines(cropGuideMode ?? "thirds", boundsW, boundsH, totalViewportScale)}
      <Rect
        x={0}
        y={0}
        width={boundsW}
        height={boundsH}
        fill="rgba(0,0,0,0.01)"
        draggable
        onDragStart={(evt: any) => {
          evt.cancelBubble = true;
          if (cropRect) {
            cropDragRef.current.set(el.id, { ...cropRect });
          }
        }}
        onDragMove={(evt: any) => {
          evt.cancelBubble = true;
          const base = cropDragRef.current.get(el.id) ?? cropRect;
          if (!base) return;
          const node = evt.target as any;
          const dx = Number(node.x()) || 0;
          const dy = Number(node.y()) || 0;
          if (!dx && !dy) return;
          node.position({ x: 0, y: 0 });
          const next = clampCropRect(
            {
              x: base.x + dx,
              y: base.y + dy,
              width: base.width,
              height: base.height,
            },
            boundsW,
            boundsH
          );
          cropDragRef.current.set(el.id, next);
          updateEl(el.id, { contentRect: next });
        }}
        onDragEnd={(evt: any) => {
          evt.cancelBubble = true;
          const node = evt.target as any;
          node.position({ x: 0, y: 0 });
          cropDragRef.current.delete(el.id);
        }}
      />
    </Group>
  ) : null;

  return (
    <Group key={el.id} {...common} width={boundsW} height={boundsH}>
      {bgLayer}
      {containBgLayer}
      {maskEnabled && maskFeather > 0.001 ? (
        <FeatheredGroup blurRadius={maskFeather} cacheKey={clipCacheKey} groupProps={maskClipProps} isTransformingRef={isTransformingRef}>
          <FilteredImage
            common={{ x: drawX, y: drawY, globalCompositeOperation }}
            width={drawW}
            height={drawH}
            image={img || undefined}
            elementId={el.id}
            sourceMedia={originalImg || sourceMediaFallback}
            faceBlur={faceBlurSettings}
            filters={konvaFilters}
            filterProps={filterProps}
            hasFilters={konvaFilters.length > 0}
            crop={crop}
            isTransformingRef={isTransformingRef}
            gpuFilters={gpuFilters}
            filterSignature={imageFilterSignature}
          />
        </FeatheredGroup>
      ) : (
        <Group {...maskClipProps}>
          <FilteredImage
            common={{ x: drawX, y: drawY, globalCompositeOperation }}
            width={drawW}
            height={drawH}
            image={img || undefined}
            elementId={el.id}
            sourceMedia={originalImg || sourceMediaFallback}
            faceBlur={faceBlurSettings}
            filters={konvaFilters}
            filterProps={filterProps}
            hasFilters={konvaFilters.length > 0}
            crop={crop}
            isTransformingRef={isTransformingRef}
            gpuFilters={gpuFilters}
            filterSignature={imageFilterSignature}
          />
        </Group>
      )}
      {cropOverlay}

      {needsBorder ? (
        maskEnabled ? (
          <Shape
            x={0}
            y={0}
            width={boundsW}
            height={boundsH}
            fillEnabled={false}
            stroke={borderStroke as any}
            strokeWidth={borderW}
            dash={borderDash}
            strokeScaleEnabled={false}
            listening
            sceneFunc={(ctx, shape) => {
              drawMaskPathOnCtx(ctx as any, boundsW, boundsH, mask, radiiPx, curve);
              ctx.fillStrokeShape(shape);
            }}
          />
        ) : curve ? (
          <Shape
            x={alignDelta}
            y={alignDelta}
            width={borderW2}
            height={borderH2}
            fillEnabled={false}
            stroke={borderStroke as any}
            strokeWidth={borderW}
            dash={borderDash}
            strokeScaleEnabled={false}
            listening
            sceneFunc={(ctx, shape) => {
              roundedRectWithCurvePath(ctx as any, borderW2, borderH2, borderRadii, curve, cornerCurve);
              ctx.fillStrokeShape(shape);
            }}
          />
        ) : (
          <Rect
            x={alignDelta}
            y={alignDelta}
            width={borderW2}
            height={borderH2}
            fill="rgba(0,0,0,0)"
            stroke={borderStroke as any}
            strokeWidth={borderW}
            dash={borderDash}
            cornerRadius={konvaCornerRadiusProp(borderRadii) as any}
            strokeScaleEnabled={false}
            listening
          />
        )
      ) : null}

      {showMaskOutline ? (
        <Shape
          x={0}
          y={0}
          width={boundsW}
          height={boundsH}
          fillEnabled={false}
          stroke="rgba(59,130,246,0.9)"
          strokeWidth={Math.max(1, 1 / Math.max(0.001, totalViewportScale))}
          dash={[6, 4]}
          strokeScaleEnabled={false}
          listening={false}
          perfectDrawEnabled={false}
          sceneFunc={(ctx, shape) => {
            drawMaskPathOnCtx(ctx as any, boundsW, boundsH, mask, radiiPx, curve);
            ctx.fillStrokeShape(shape);
          }}
        />
      ) : null}
    </Group>
  );
}

export type RenderVideoElementArgs = {
  el: VideoEl;
  common: KonvaNodeCommonProps;
  globalCompositeOperation: GlobalCompositeOperation;
  runtime: React.MutableRefObject<RuntimeMedia>;
  faceBlurCacheRef: React.MutableRefObject<Map<string, HTMLCanvasElement | ImageBitmap | null>>;
  gradientFadeCacheRef?: React.MutableRefObject<Map<string, ImageBitmap | null>>;
  hoveredElementId: string | null;
  totalViewportScale: number;
  isTransformingRef: React.RefObject<boolean>;
  cropGuidesEnabled: boolean;
  cropGuideMode?: string;
  isCropTarget: boolean;
  cropDragRef: React.MutableRefObject<Map<string, { x: number; y: number; width: number; height: number }>>;
  updateEl: (id: string, patch: Partial<Element>) => void;
  videoFilterPreviewQuality: VideoFilterPreviewQuality;
  previewTraceEnabled: boolean;
  isPlaying: boolean;
};

export function renderVideoElement({
  el,
  common,
  globalCompositeOperation,
  runtime,
  faceBlurCacheRef,
  gradientFadeCacheRef,
  hoveredElementId,
  totalViewportScale,
  isTransformingRef,
  cropGuidesEnabled,
  cropGuideMode,
  isCropTarget,
  cropDragRef,
  updateEl,
  videoFilterPreviewQuality,
  previewTraceEnabled,
  isPlaying,
}: RenderVideoElementArgs) {
  const wcCanvas = runtime.current.webCodecsCanvasMap?.get(el.id);
  const vid = runtime.current.videoMap.get(el.id);
  const posterImg = runtime.current.imageMap.get(el.id);
  const originalSrc = wcCanvas ?? vid;
  // D4 — during pause/scrub, prefer the cached scrub bitmap (staged by
  // timeline/engine.ts:syncVideosAtPlayhead from VideoRegistry's
  // frameCache). During play, the live video element is authoritative.
  // The scrub bitmap is owned by VideoRegistry; we only read it here.
  // Kept SEPARATE from originalSrc because ImageBitmap isn't in the
  // FilteredImage.sourceMedia type union (HTMLImage/Video/Canvas only);
  // we only override the `image` prop below, not `sourceMedia`.
  const scrubBitmap = !isPlaying ? getScrubBitmap(el.id) : null;
  const videoEl = el;
  const videoStatus = vid ? getHtmlVideoStatus(vid) : "missing";
  const posterStatus = posterImg ? getHtmlImageStatus(posterImg) : "missing";
  // Only fall back to the poster when there is genuinely no video source yet or
  // the video is explicitly deferred (background proxy prep).
  // Do NOT fall back when videoStatus is temporarily "not-ready" during a seek/scrub —
  // that would freeze the canvas on the poster image while the decoder catches up.
  // Keeping the last decoded video frame visible is always better than switching to a static poster.
  const shouldUsePosterFallback =
    posterStatus === "ready" &&
    ((videoEl as any).deferVideoHydration === true || !originalSrc);

  // Use face blur processed canvas if available, otherwise original source
  const videoFaceBlurSettings = (videoEl as any).faceBlur;
  const hasVideoFaceBlur = needsFaceBlurProcessing(videoFaceBlurSettings);
  const videoFaceBlurCanvas = hasVideoFaceBlur ? faceBlurCacheRef.current.get(el.id) : null;
  const protectLongVideoPlayback = shouldProtectLongVideoPlayback({
    durationSec: videoEl.preparedMedia?.duration,
    previewUrl: videoEl.previewUrl,
    srcUrl: videoEl.src,
    deferVideoHydration: videoEl.deferVideoHydration,
    isPlaying,
  });
  // Note: For videos, face blur is applied to a snapshot. Real-time blur requires frame-by-frame processing.
  // D4: if the scrub bitmap is staged, it wins over everything except
  // face-blur (which must run on the live pipeline). This makes paused
  // scrub feel instant — no wait for seek → decode → drawImage round-trip.
  const src = videoFaceBlurCanvas
    || scrubBitmap
    || (shouldUsePosterFallback ? posterImg : originalSrc);

  // Check for gradient fade effect
  const gradientFadeBitmap = gradientFadeCacheRef?.current.get(el.id);
  const opSettings = (el as any).opacitySettings;
  const useGradientFade = !protectLongVideoPlayback && opSettings?.mode === 'gradient-fade' && gradientFadeBitmap;

  // If gradient fade is active, render the pre-composited bitmap
  if (useGradientFade && gradientFadeBitmap) {
    return (
      <KImage
        key={el.id}
        {...common}
        image={gradientFadeBitmap}
        width={el.width}
        height={el.height}
        vbElementId={el.id}
        vbSource={originalSrc || undefined}
        vbVideo={vid || undefined}
        vbFaceBlur={videoFaceBlurSettings}
        globalCompositeOperation={globalCompositeOperation}
      />
    );
  }

  // Check if this is a placeholder video (no source loaded)
  const isPlaceholder = !src || !videoEl.src || videoEl.src === "" || videoEl.src.startsWith("{{");

  // FX overlays: skip placeholder — the screen-blend + black Stage background creates a
  // dark artefact that blocks the video beneath. Let the video render directly (showing
  // whatever frame is available, which is correct for scrubbing and initial load).
  const isFxOverlay = !!(videoEl as any).fxOverlayMeta?.enabled;

  if (isPlaceholder) {
    // Render placeholder for video elements without source
    const placeholderName = videoEl.name || el.id;
    const label = placeholderName.includes("{{") ? placeholderName : "Drop video here";
    return (
      <Group key={el.id} {...common} width={el.width} height={el.height}>
        <Rect
          x={0}
          y={0}
          width={el.width}
          height={el.height}
          fill="#1a1a2e"
          stroke="rgba(251,191,36,0.4)"
          strokeWidth={2}
          dash={[8, 4]}
          cornerRadius={4}
        />
        <KText
          x={0}
          y={0}
          width={el.width}
          height={el.height}
          text={label}
          fontSize={Math.min(24, Math.max(12, el.width / 12))}
          fill="rgba(255,255,255,0.6)"
          align="center"
          verticalAlign="middle"
          listening={false}
        />
      </Group>
    );
  }

  const boundsW = Math.max(1, Number(el.width) || 1);
  const boundsH = Math.max(1, Number(el.height) || 1);
  const radiiPx = resolveCornerRadiiPx(videoEl.cornerRadius, boundsW, boundsH, videoEl);
  const curve = normalizeCurve(videoEl);
  const cornerCurve = normalizeCornerCurve(videoEl);
  const border = normalizeBorder(videoEl);
  const borderW = border.enabled ? border.width : 0;
  const borderStroke = border.color ? colorWithAlpha(border.color, border.opacity) : undefined;
  const borderDash = borderDashForStyle(border.style, borderW);
  const mask = (videoEl as any)?.mask as any;
  const maskEnabled = !!mask?.enabled;
  const needsRoundedClip = radiiPx.some((v) => v > 0.001);
  const needsCustomClip = needsRoundedClip || !!curve;
  // Media rule: border renders outside the content mask.
  const alignDelta = borderAlignDeltaPx("outside", borderW);
  const borderW2 = Math.max(1, boundsW - 2 * alignDelta);
  const borderH2 = Math.max(1, boundsH - 2 * alignDelta);
  const borderRadii = radiiPx.map((r) => Math.max(0, r - alignDelta)) as [number, number, number, number];
  const needsBorder = borderW > 0.001 && !!borderStroke;

  // FIX: Use stored naturalWidth/naturalHeight from element state for stable autoFitRect
  // This prevents the video from "jumping" when HTMLVideoElement.videoWidth/videoHeight
  // become available after the video loads. The stored dimensions are set on initial insert.
  const storedNaturalW = (videoEl as any).naturalWidth;
  const storedNaturalH = (videoEl as any).naturalHeight;

  // When using poster image as fallback (e.g. during scrub seek), use the poster's own
  // naturalWidth/naturalHeight for the crop — NOT the stored video dimensions.
  // Using stored video dims (e.g. 1920×1080) as a Konva crop on a small poster thumbnail
  // (e.g. 160×90) causes Konva to sample a region much larger than the image, showing only
  // a tiny fragment of the poster in the top-left corner of the canvas element.
  const isPosterFallback = shouldUsePosterFallback && !!posterImg;
  const liveIw = isPosterFallback
    ? (posterImg!.naturalWidth || 0)
    : (typeof HTMLVideoElement !== "undefined" && src instanceof HTMLVideoElement ? src.videoWidth || 0 : (src as HTMLCanvasElement).width || 0);
  const liveIh = isPosterFallback
    ? (posterImg!.naturalHeight || 0)
    : (typeof HTMLVideoElement !== "undefined" && src instanceof HTMLVideoElement ? src.videoHeight || 0 : (src as HTMLCanvasElement).height || 0);
  // For poster fallback: use poster dims directly (0 = no crop = let Konva scale freely).
  // For video source: prefer stored dims for stability, fallback to live dims.
  const iw = isPosterFallback
    ? liveIw
    : ((typeof storedNaturalW === "number" && storedNaturalW > 0) ? storedNaturalW : liveIw);
  const ih = isPosterFallback
    ? liveIh
    : ((typeof storedNaturalH === "number" && storedNaturalH > 0) ? storedNaturalH : liveIh);

  const rawContentRect = (videoEl as any)?.contentRect as { x: number; y: number; width: number; height: number } | undefined;

  // Build Konva filters from our filter format (including HSL/curves from photoAdjustments and LUT)
  const elementFilters = videoEl.filters || [];
  const vidLutSettings = videoEl.lutSettings;
  const effectivePhotoAdjustments = protectLongVideoPlayback ? undefined : videoEl.photoAdjustments;
  const effectiveElementFilters = protectLongVideoPlayback ? [] : elementFilters;
  const effectiveLutSettings = protectLongVideoPlayback ? undefined : vidLutSettings;
  const videoFilterSignature = generateFilterSignature(
    effectiveElementFilters,
    effectivePhotoAdjustments,
    undefined,
    effectiveLutSettings
  );
  const {
    konvaFilters,
    props: filterProps,
    gpuFilters,
  } = buildKonvaFilters(
    effectiveElementFilters,
    effectivePhotoAdjustments,
    undefined,
    effectiveLutSettings
  );

  const hasRawContentRect =
    !!rawContentRect &&
    Number.isFinite(rawContentRect.x) &&
    Number.isFinite(rawContentRect.y) &&
    Number.isFinite(rawContentRect.width) &&
    Number.isFinite(rawContentRect.height) &&
    rawContentRect.width > 0 &&
    rawContentRect.height > 0;

  // Use element's fitMode (default to 'contain' to prevent unwanted zoom)
  const fitMode = (videoEl as any).fitMode === "cover" ? "cover" : "contain";
  // Calculate autoFitRect in NATURAL source coordinates (for crop), not frame coordinates
  const autoFitRect =
    !hasRawContentRect && iw > 0 && ih > 0
      ? (() => {
          if (fitMode === "contain") {
            // CONTAIN: Show full source (no crop)
            return { x: 0, y: 0, width: iw, height: ih };
          } else {
            // COVER: Calculate which portion of source fills the frame
            const frameAspect = boundsW / boundsH;
            const mediaAspect = iw / ih;
            if (mediaAspect > frameAspect) {
              // Media is wider - crop sides
              const visibleWidth = ih * frameAspect;
              const cropX = (iw - visibleWidth) / 2;
              return { x: cropX, y: 0, width: visibleWidth, height: ih };
            } else {
              // Media is taller - crop top/bottom
              const visibleHeight = iw / frameAspect;
              const cropY = (ih - visibleHeight) / 2;
              return { x: 0, y: cropY, width: iw, height: visibleHeight };
            }
          }
        })()
      : undefined;

  const contentRect = hasRawContentRect ? rawContentRect : autoFitRect;

  const hasContentRect =
    !!contentRect &&
    Number.isFinite(contentRect.x) &&
    Number.isFinite(contentRect.y) &&
    Number.isFinite(contentRect.width) &&
    Number.isFinite(contentRect.height) &&
    contentRect.width > 0 &&
    contentRect.height > 0;

  const onMediaDblClick = !shouldUsePosterFallback && vid
    ? () => {
        if (vid.paused) {
          vid.play().catch(() => {});
        } else {
          vid.pause();
        }
      }
    : undefined;

  // Calculate crop for Image element - always use Image's built-in crop
  // This is more reliable than Group clipping which doesn't work in all cases
  const crop = hasContentRect && iw > 0 && ih > 0
    ? (() => {
        // contentRect is in natural video coordinates
        // Clamp to valid bounds
        const cropX = Math.max(0, Math.min(iw, contentRect.x));
        const cropY = Math.max(0, Math.min(ih, contentRect.y));
        const cropW = Math.max(1, Math.min(iw - cropX, contentRect.width));
        const cropH = Math.max(1, Math.min(ih - cropY, contentRect.height));
        return { x: cropX, y: cropY, width: cropW, height: cropH };
      })()
    : undefined;

  // Draw position: always at 0,0 since crop handles the source selection
  const drawX = 0;
  const drawY = 0;
  // Draw size: frame bounds - Image will scale the cropped source to fit
  const drawW = boundsW;
  const drawH = boundsH;

  const maskClipProps = maskEnabled
    ? {
        clipFunc: maskClipFunc({ w: boundsW, h: boundsH, mask, radiiPx, curve }),
      }
    : (needsCustomClip
        ? {
            clipFunc: (ctx: any) => {
              roundedRectWithCurvePath(ctx as any, boundsW, boundsH, radiiPx, curve, cornerCurve);
            },
          }
        : undefined);

  const showMaskOutline = maskEnabled && hoveredElementId === el.id;

  const cropRect = hasContentRect ? contentRect : autoFitRect;
  const cropOverlay = isCropTarget && cropRect ? (
    <Group>
      <Rect
        x={0}
        y={0}
        width={boundsW}
        height={boundsH}
        stroke="rgba(251, 191, 36, 0.9)"
        strokeWidth={Math.max(1, 1 / Math.max(0.001, totalViewportScale))}
        listening={false}
      />
      {cropGuidesEnabled && renderCropGuideLines(cropGuideMode ?? "thirds", boundsW, boundsH, totalViewportScale)}
      <Rect
        x={0}
        y={0}
        width={boundsW}
        height={boundsH}
        fill="rgba(0,0,0,0.01)"
        draggable
        onDragStart={(evt: any) => {
          evt.cancelBubble = true;
          if (cropRect) {
            cropDragRef.current.set(el.id, { ...cropRect });
          }
        }}
        onDragMove={(evt: any) => {
          evt.cancelBubble = true;
          const base = cropDragRef.current.get(el.id) ?? cropRect;
          if (!base) return;
          const node = evt.target as any;
          const dx = Number(node.x()) || 0;
          const dy = Number(node.y()) || 0;
          if (!dx && !dy) return;
          node.position({ x: 0, y: 0 });
          const next = clampCropRect(
            {
              x: base.x + dx,
              y: base.y + dy,
              width: base.width,
              height: base.height,
            },
            boundsW,
            boundsH
          );
          cropDragRef.current.set(el.id, next);
          updateEl(el.id, { contentRect: next });
        }}
        onDragEnd={(evt: any) => {
          evt.cancelBubble = true;
          const node = evt.target as any;
          node.position({ x: 0, y: 0 });
          cropDragRef.current.delete(el.id);
        }}
      />
    </Group>
  ) : null;

  // Contain mode background (letterbox/pillarbox fill)
  const containBgStyle = (videoEl as any).containBgStyle || "transparent";
  const containBgColor = (videoEl as any).containBgColor || "#000000";
  const containBgBlur = (videoEl as any).containBgBlur ?? 20;
  const needsContainBg = fitMode === "contain" && containBgStyle !== "transparent" && hasContentRect;

  const containBgLayer = needsContainBg ? (
    containBgStyle === "color" ? (
      <Rect
        x={0}
        y={0}
        width={boundsW}
        height={boundsH}
        fill={containBgColor}
        listening={false}
      />
    ) : containBgStyle === "blur" && src ? (
      <KImage
        x={0}
        y={0}
        width={boundsW}
        height={boundsH}
        image={src}
        filters={[Konva.Filters.Blur]}
        blurRadius={containBgBlur}
        listening={false}
      />
    ) : containBgStyle === "mirror" && src ? (
      // Mirror edges: scale up and blur - simplified version
      <KImage
        x={-boundsW * 0.1}
        y={-boundsH * 0.1}
        width={boundsW * 1.2}
        height={boundsH * 1.2}
        image={src}
        filters={[Konva.Filters.Blur]}
        blurRadius={10}
        listening={false}
      />
    ) : null
  ) : null;

  return (
    <Group key={el.id} {...common} width={boundsW} height={boundsH}>
      {containBgLayer}
      <Group {...maskClipProps}>
        <FilteredImage
          common={{ x: drawX, y: drawY, globalCompositeOperation }}
          width={drawW}
          height={drawH}
          image={src}
          elementId={el.id}
          sourceMedia={originalSrc || undefined}
          sourceVideo={vid || undefined}
          faceBlur={videoFaceBlurSettings}
          filters={konvaFilters}
          filterProps={filterProps}
          hasFilters={konvaFilters.length > 0}
          isVideoElement
          debugId={previewTraceEnabled ? el.id : undefined}
          crop={crop}
          onDblClick={onMediaDblClick}
          isTransformingRef={isTransformingRef}
          gpuFilters={gpuFilters}
          filterSignature={videoFilterSignature}
          lutSettings={vidLutSettings}
          previewQuality={videoFilterPreviewQuality}
        />
      </Group>
      {cropOverlay}

      {needsBorder ? (
        maskEnabled ? (
          <Shape
            x={0}
            y={0}
            width={boundsW}
            height={boundsH}
            fillEnabled={false}
            stroke={borderStroke as any}
            strokeWidth={borderW}
            dash={borderDash}
            strokeScaleEnabled={false}
            listening
            sceneFunc={(ctx, shape) => {
              drawMaskPathOnCtx(ctx as any, boundsW, boundsH, mask, radiiPx, curve);
              ctx.fillStrokeShape(shape);
            }}
          />
        ) : curve ? (
          <Shape
            x={alignDelta}
            y={alignDelta}
            width={borderW2}
            height={borderH2}
            fillEnabled={false}
            stroke={borderStroke as any}
            strokeWidth={borderW}
            dash={borderDash}
            strokeScaleEnabled={false}
            listening
            sceneFunc={(ctx, shape) => {
              roundedRectWithCurvePath(ctx as any, borderW2, borderH2, borderRadii, curve, cornerCurve);
              ctx.fillStrokeShape(shape);
            }}
          />
        ) : (
          <Rect
            x={alignDelta}
            y={alignDelta}
            width={borderW2}
            height={borderH2}
            fill="rgba(0,0,0,0)"
            stroke={borderStroke as any}
            strokeWidth={borderW}
            dash={borderDash}
            cornerRadius={konvaCornerRadiusProp(borderRadii) as any}
            strokeScaleEnabled={false}
            listening
          />
        )
      ) : null}

      {showMaskOutline ? (
        <Shape
          x={0}
          y={0}
          width={boundsW}
          height={boundsH}
          fillEnabled={false}
          stroke="rgba(59,130,246,0.9)"
          strokeWidth={Math.max(1, 1 / Math.max(0.001, totalViewportScale))}
          dash={[6, 4]}
          strokeScaleEnabled={false}
          listening={false}
          perfectDrawEnabled={false}
          sceneFunc={(ctx, shape) => {
            drawMaskPathOnCtx(ctx as any, boundsW, boundsH, mask, radiiPx, curve);
            ctx.fillStrokeShape(shape);
          }}
        />
      ) : null}

      {/* Progress ring: shown while video is deferred (MOV background normalization) */}
      {shouldUsePosterFallback && (videoEl as any).deferVideoHydration === true ? (
        <Shape
          x={0}
          y={0}
          width={boundsW}
          height={boundsH}
          listening={false}
          perfectDrawEnabled={false}
          sceneFunc={(ctx) => {
            const nctx = (ctx as any)._context as CanvasRenderingContext2D;
            const ringR = Math.max(10, Math.min(18, boundsW * 0.05));
            const pad = ringR + 6;
            const cx = boundsW - pad;
            const cy = pad;
            const lineW = Math.max(2, ringR / 4.5);
            const angle = (typeof performance !== "undefined" ? performance.now() : Date.now()) / 700;
            nctx.save();
            // Dark pill background
            nctx.beginPath();
            nctx.arc(cx, cy, ringR + lineW + 2, 0, Math.PI * 2);
            nctx.fillStyle = "rgba(0,0,0,0.55)";
            nctx.fill();
            // Amber spinning arc (270°)
            nctx.beginPath();
            nctx.arc(cx, cy, ringR, angle, angle + Math.PI * 1.6);
            nctx.strokeStyle = "rgba(245,158,11,0.95)";
            nctx.lineWidth = lineW;
            nctx.lineCap = "round";
            nctx.stroke();
            nctx.restore();
          }}
        />
      ) : null}
    </Group>
  );
}
